import { supabase } from '@/lib/supabase';
import { unwrap, ServiceError } from '@/data/serviceError';
import type { TablesInsert } from '@/types/db';
import type {
  Actie,
  ActieInvulling,
  Bouwpunt,
  BouwpuntStatus,
  Melding,
  MeldingPatch,
} from '../types';
import type { ActieSoort } from '../lib/acties';

/** 'beheerder' = Tom (schrijft), 'meekijker' = aangewezen lezer (Mike), null = geen toegang. */
export type SoftwareLijstRol = 'beheerder' | 'meekijker' | null;

export interface Meekijker {
  user_id: string;
  created_at: string;
  profiel: { full_name: string | null; email: string; role: string } | null;
}

export interface MeekijkerKandidaat {
  id: string;
  full_name: string | null;
  email: string;
  role: string;
}

/**
 * Data-toegang voor de softwarelijst (bouwlijst + teammeldingen): wie mag
 * meekijken, en de uitleg "wat is aangepast en hoe werkt het" per punt.
 * De rolbepaling zit in de database (software_lijst_rol), niet in de frontend.
 */
export const softwareLijstService = {
  async rol(): Promise<SoftwareLijstRol> {
    const rol = unwrap(await supabase.rpc('software_lijst_rol'), 'Softwarelijst-rol bepalen');
    return rol === 'beheerder' || rol === 'meekijker' ? rol : null;
  },

  async meekijkers(): Promise<Meekijker[]> {
    return unwrap(
      await supabase
        .from('software_lijst_meekijkers')
        .select(
          'user_id, created_at, profiel:profiles!software_lijst_meekijkers_user_id_fkey(full_name, email, role)',
        )
        .order('created_at'),
      'Meekijkers laden',
    ) as unknown as Meekijker[];
  },

  /** Wie aangewezen kan worden: alleen Admin-accounts (de lijst is voor de eigenaren). */
  async kandidaten(): Promise<MeekijkerKandidaat[]> {
    return unwrap(
      await supabase
        .from('profiles')
        .select('id, full_name, email, role')
        .eq('role', 'Admin')
        .order('full_name'),
      'Kandidaten laden',
    );
  },

  async voegMeekijkerToe(userId: string, doorId: string): Promise<void> {
    const { error } = await supabase
      .from('software_lijst_meekijkers')
      .insert({ user_id: userId, toegevoegd_door: doorId });
    if (error) throw new ServiceError(`Meekijker toevoegen: ${error.message}`, error.code);
  },

  async verwijderMeekijker(userId: string): Promise<void> {
    const { error } = await supabase
      .from('software_lijst_meekijkers')
      .delete()
      .eq('user_id', userId);
    if (error) throw new ServiceError(`Meekijker verwijderen: ${error.message}`, error.code);
  },

  // ---- Meldingen (software_meldingen) ------------------------------------

  /** Alle meldingen, nieuwste eerst. RLS: melder ziet eigen, beheerder en meekijkers alles. */
  async meldingen(): Promise<Melding[]> {
    return unwrap(
      await supabase
        .from('software_meldingen')
        .select('*')
        .order('created_at', { ascending: false }),
      'Meldingen laden',
    );
  },

  /** Verplaatsing in de pijplijn (status / bouw_status / beoordeeld_op); alleen de beheerder. */
  async verplaatsMelding(id: string, patch: MeldingPatch): Promise<void> {
    const { error } = await supabase.from('software_meldingen').update(patch).eq('id', id);
    if (error) throw new ServiceError(`Melding verplaatsen: ${error.message}`, error.code);
  },

  /** Beoordeling (reactie aan de melder) + status in één keer, zoals de oude knoppen deden. */
  async beoordeelMelding(
    id: string,
    status: 'gepland' | 'opgelost' | 'afgewezen',
    beoordeling: string | null,
  ): Promise<void> {
    const { error } = await supabase
      .from('software_meldingen')
      .update({
        status,
        beoordeling,
        beoordeeld_op: new Date().toISOString(),
        // Opgelost/afgewezen: de bouw is klaar of komt er niet.
        ...(status === 'gepland' ? {} : { bouw_status: null }),
      })
      .eq('id', id);
    if (error) throw new ServiceError(`Beoordeling opslaan: ${error.message}`, error.code);
  },

  /** Alleen de reactietekst aan de melder (zonder statuswissel). */
  async bewaarBeoordeling(id: string, beoordeling: string | null): Promise<void> {
    const { error } = await supabase
      .from('software_meldingen')
      .update({ beoordeling })
      .eq('id', id);
    if (error) throw new ServiceError(`Reactie opslaan: ${error.message}`, error.code);
  },

  /** Knop "In bouw": pijplijnstap plus optionele PR-link. */
  async zetInBouw(id: string, prUrl: string | null): Promise<void> {
    const { error } = await supabase
      .from('software_meldingen')
      .update({
        status: 'gepland',
        bouw_status: 'in_bouw',
        pr_url: prUrl,
        beoordeeld_op: new Date().toISOString(),
      })
      .eq('id', id);
    if (error) throw new ServiceError(`In bouw zetten: ${error.message}`, error.code);
  },

  async bewaarPrUrl(id: string, prUrl: string | null): Promise<void> {
    const { error } = await supabase
      .from('software_meldingen')
      .update({ pr_url: prUrl })
      .eq('id', id);
    if (error) throw new ServiceError(`PR-link opslaan: ${error.message}`, error.code);
  },

  /** Tijdelijke leeslink voor een schermafbeelding of opname in de bucket documents. */
  async bestandUrl(pad: string): Promise<string | null> {
    const { data, error } = await supabase.storage.from('documents').createSignedUrl(pad, 3600);
    if (error) throw new ServiceError(`Bestand ophalen: ${error.message}`);
    return data?.signedUrl ?? null;
  },

  // ---- Bouwlijst (tom_build_reviews) --------------------------------------

  async bouwpunten(): Promise<Bouwpunt[]> {
    return unwrap(
      await supabase.from('tom_build_reviews').select('*').order('sort_order'),
      'Bouwlijst laden',
    );
  },

  async voegBouwpuntToe(titel: string, module: string): Promise<void> {
    const rij: TablesInsert<'tom_build_reviews'> = {
      titel,
      module,
      status: 'gepland',
      sort_order: 9999,
    };
    const { error } = await supabase.from('tom_build_reviews').insert(rij);
    if (error) throw new ServiceError(`Bouwpunt toevoegen: ${error.message}`, error.code);
  },

  async zetBouwpuntStatus(id: string, status: BouwpuntStatus): Promise<void> {
    const { error } = await supabase.from('tom_build_reviews').update({ status }).eq('id', id);
    if (error) throw new ServiceError(`Status wijzigen: ${error.message}`, error.code);
  },

  /**
   * Commentaar voor Claude. Commentaar zonder expliciete beoordeling zet het
   * punt op 'feedback', zodat het bij de volgende werksessie opvalt.
   */
  async bewaarCommentaar(b: Pick<Bouwpunt, 'id' | 'status'>, tekst: string | null): Promise<void> {
    const { error } = await supabase
      .from('tom_build_reviews')
      .update({
        commentaar: tekst,
        commentaar_op: tekst ? new Date().toISOString() : null,
        ...(tekst && b.status !== 'akkoord' && b.status !== 'feedback'
          ? { status: 'feedback' }
          : {}),
      })
      .eq('id', b.id);
    if (error) throw new ServiceError(`Commentaar opslaan: ${error.message}`, error.code);
  },

  // ---- Persoonlijke acties (tom_actielijst, privé) -------------------------

  async acties(): Promise<Actie[]> {
    return unwrap(
      await supabase.from('tom_actielijst').select('*').order('sort_order').order('created_at'),
      'Acties laden',
    );
  },

  async voegActieToe(titel: string, soort: ActieSoort = 'actie'): Promise<void> {
    const { error } = await supabase
      .from('tom_actielijst')
      .insert({ titel, soort, module: 'algemeen', eigenaar: 'Tom', sort_order: 999 });
    if (error) throw new ServiceError(`Actie toevoegen: ${error.message}`, error.code);
  },

  async zetActieStatus(id: string, status: 'open' | 'afgerond'): Promise<void> {
    const { error } = await supabase
      .from('tom_actielijst')
      .update({ status, afgerond_op: status === 'afgerond' ? new Date().toISOString() : null })
      .eq('id', id);
    if (error) throw new ServiceError(`Actie bijwerken: ${error.message}`, error.code);
  },

  /** Tom's invulling van één punt: keuze per vraag, toelichting, bouwbesluit, cluster. */
  async werkActieBij(id: string, patch: ActieInvulling): Promise<void> {
    const { error } = await supabase.from('tom_actielijst').update(patch).eq('id', id);
    if (error) throw new ServiceError(`Punt opslaan: ${error.message}`, error.code);
  },

  /** "Akkoord: hele fase bouwen" — alleen punten zonder besluit; eerdere keuzes blijven. */
  async keurSoftwareGoed(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await supabase
      .from('tom_actielijst')
      .update({ besluit: 'bouwen' })
      .in('id', ids)
      .is('besluit', null);
    if (error) throw new ServiceError(`Fase goedkeuren: ${error.message}`, error.code);
  },

  async bewaarUitlegBouwpunt(id: string, uitleg: string | null): Promise<void> {
    const { error } = await supabase.from('tom_build_reviews').update({ uitleg }).eq('id', id);
    if (error) throw new ServiceError(`Uitleg opslaan: ${error.message}`, error.code);
  },

  async bewaarUitlegMelding(id: string, uitleg: string | null): Promise<void> {
    const { error } = await supabase.from('software_meldingen').update({ uitleg }).eq('id', id);
    if (error) throw new ServiceError(`Uitleg opslaan: ${error.message}`, error.code);
  },

  /**
   * De melder past zijn eigen melding aan zolang die nieuw en onbeoordeeld is
   * (verzoek Pieter 30-09). Welke kolommen mogen bewaakt de database-trigger
   * bewaak_melding_update; de foutmelding daarvan is al in gewone taal.
   */
  async bewerkEigenMelding(id: string, velden: EigenMeldingWijziging): Promise<void> {
    const omschrijving = velden.omschrijving.trim();
    if (!omschrijving) throw new ServiceError('Omschrijf eerst wat er niet klopt of wat je wilt.');
    const { error } = await supabase
      .from('software_meldingen')
      .update({
        soort: velden.soort,
        omschrijving,
        stappen: velden.soort === 'probleem' ? velden.stappen.trim() || null : null,
        ernst: velden.soort === 'probleem' ? velden.ernst : null,
      })
      .eq('id', id);
    if (error) throw new ServiceError(`Melding aanpassen: ${error.message}`, error.code);
  },
};

export type MeldingSoort = 'probleem' | 'verzoek';
export type MeldingErnst = 'blokkeert' | 'hinderlijk' | 'klein';

export interface EigenMeldingWijziging {
  soort: MeldingSoort;
  omschrijving: string;
  stappen: string;
  ernst: MeldingErnst | null;
}
